import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card.jsx';
import { Badge } from './ui/badge.jsx';
import PaginationWrapper from './PaginationWrapper';
import { 
  ArrowLeft, 
  Activity, 
  Search,
  Eye,
  Edit,
  Trash2,
  LogIn,
  Copy,
  Plus,
  Clock,
  User,
  Building
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ActivityLogs = () => {
  const { user } = useAuth();
  const { companyId } = useParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    skip: 0,
    limit: 20,
    has_more: false
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCompanies();
    fetchActivities();
  }, [companyId, actionFilter, currentPage]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/companies');
      setCompanies(response.data);
      
      if (companyId) {
        const company = response.data.find(c => c.id === companyId);
        setSelectedCompany(company);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const skip = (currentPage - 1) * 20;
      let url = '/activities';
      const params = new URLSearchParams();
      
      if (companyId) {
        url = `/activities/companies/${companyId}`;
      } else {
        if (actionFilter) params.append('action', actionFilter);
        params.append('skip', skip.toString());
        params.append('limit', '20');
        if (params.toString()) url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      
      if (response.data.activities) {
        setActivities(response.data.activities);
        setPagination({
          total: response.data.total,
          skip: response.data.skip,
          limit: response.data.limit,
          has_more: response.data.has_more
        });
      } else {
        // Fallback for old API response format
        setActivities(response.data);
      }
    } catch (error) {
      toast.error('Error fetching activity logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'view': return <Eye className="h-4 w-4" />;
      case 'edit': return <Edit className="h-4 w-4" />;
      case 'delete': return <Trash2 className="h-4 w-4" />;
      case 'create': return <Plus className="h-4 w-4" />;
      case 'auto_login': return <LogIn className="h-4 w-4" />;
      case 'copy_credentials': return <Copy className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'view': return 'bg-blue-100 text-blue-800';
      case 'edit': return 'bg-yellow-100 text-yellow-800';
      case 'delete': return 'bg-red-100 text-red-800';
      case 'create': return 'bg-green-100 text-green-800';
      case 'auto_login': return 'bg-purple-100 text-purple-800';
      case 'copy_credentials': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'view': return 'Viewed';
      case 'edit': return 'Edited';
      case 'delete': return 'Deleted';
      case 'create': return 'Created';
      case 'auto_login': return 'Auto Login';
      case 'copy_credentials': return 'Copied Credentials';
      default: return action;
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown Company';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setLoading(true);
  };

  const filteredActivities = activities.filter(activity =>
    activity.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getCompanyName(activity.company_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading activity logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-2.5 rounded-xl shadow-lg">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {selectedCompany ? `${selectedCompany.name} Activity` : 'Activity Logs'}
                </h1>
                <p className="text-sm text-gray-500">
                  {selectedCompany ? 'Company access history' : 'Track your company access activity'}
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="px-3 py-1">
              {filteredActivities.length} activities
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {!companyId && (
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md bg-white"
            >
              <option value="">All Actions</option>
              <option value="auto_login">Auto Login</option>
              <option value="copy_credentials">Copy Credentials</option>
              <option value="view">View</option>
              <option value="edit">Edit</option>
              <option value="create">Create</option>
              <option value="delete">Delete</option>
            </select>
          )}
        </div>

        {/* Activity List */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredActivities.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredActivities.map((activity) => (
                  <div key={activity.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full ${getActionColor(activity.action)}`}>
                          {getActionIcon(activity.action)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-medium text-gray-900">
                              {getActionLabel(activity.action)}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {getCompanyName(activity.company_id)}
                            </Badge>
                          </div>
                          <p className="text-gray-600 text-sm mb-2">
                            {activity.details || 'No additional details'}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>{new Date(activity.timestamp).toLocaleString()}</span>
                            </div>
                            {activity.ip_address && (
                              <div className="flex items-center space-x-1">
                                <span>IP: {activity.ip_address}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Found</h3>
                <p className="text-gray-600">
                  {companyId 
                    ? 'No activity recorded for this company yet.'
                    : 'Start using your companies to see activity logs here.'
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activities Pagination */}
        {pagination.total > 0 && (
          <PaginationWrapper
            currentPage={currentPage}
            totalPages={Math.ceil(pagination.total / pagination.limit)}
            total={pagination.total}
            limit={pagination.limit}
            skip={pagination.skip}
            onPageChange={handlePageChange}
            className="mt-6"
          />
        )}

        {/* Company Activity Summary */}
        {!companyId && companies.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Company Activity Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {companies.map((company) => {
                  const companyActivities = activities.filter(a => a.company_id === company.id);
                  const lastActivity = companyActivities[0];
                  
                  return (
                    <div key={company.id} className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{company.name}</h4>
                        <Badge variant="outline">
                          {companyActivities.length} activities
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        {lastActivity ? (
                          <div>
                            <p>Last: {getActionLabel(lastActivity.action)}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(lastActivity.timestamp).toLocaleDateString()}
                            </p>
                          </div>
                        ) : (
                          <p>No activity yet</p>
                        )}
                      </div>
                      <Link 
                        to={`/activities/${company.id}`}
                        className="mt-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                      >
                        View Details
                        <ArrowLeft className="h-3 w-3 ml-1 rotate-180" />
                      </Link>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;